<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import InboxHealthAPI from 'dashboard/api/inboxHealth';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const isLoading = ref(false);
const insights = ref(null);

const today = new Date();
const thirtyDaysAgo = new Date(today);
thirtyDaysAgo.setDate(today.getDate() - 30);

const pad = n => String(n).padStart(2, '0');
const formatDate = d =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const startDate = ref(formatDate(thirtyDaysAgo));
const endDate = ref(formatDate(today));

const providerConfig = computed(() => props.inbox.provider_config || {});
const wabaId = computed(() => providerConfig.value.business_account_id || '');
const phoneNumber = computed(() => props.inbox.phone_number || '');
const inboxName = computed(() => props.inbox.name || '');

// Template analytics
const templateData = computed(() => insights.value?.template_analytics || null);
const templateTotals = computed(() => templateData.value?.totals || null);
const templateList = computed(() => templateData.value?.templates || []);

// Pricing analytics
const pricingData = computed(() => insights.value?.pricing_analytics || null);

const pricingCategoryCards = computed(() => {
  if (!pricingData.value) return [];
  const cats = pricingData.value.by_category || {};
  const map = {
    marketing: {
      label: 'Marketing',
      icon: 'i-lucide-megaphone',
      color: 'text-n-violet-11',
      bg: 'bg-n-violet-3',
    },
    utility: {
      label: 'Utilidade',
      icon: 'i-lucide-wrench',
      color: 'text-n-blue-11',
      bg: 'bg-n-blue-3',
    },
    authentication: {
      label: 'Autenticação',
      icon: 'i-lucide-shield-check',
      color: 'text-n-amber-11',
      bg: 'bg-n-amber-3',
    },
    service: {
      label: 'Serviço',
      icon: 'i-lucide-headphones',
      color: 'text-n-teal-11',
      bg: 'bg-n-teal-3',
    },
  };
  return Object.entries(cats)
    .filter(([key]) => map[key])
    .map(([key, val]) => ({
      ...map[key],
      volume: val.volume || 0,
      cost: `$${(val.cost || 0).toFixed(2)}`,
    }));
});

// BM links
const bmBillingUrl = computed(() => {
  const waba = wabaId.value;
  if (!waba)
    return 'https://business.facebook.com/latest/billing_hub/accounts';
  return `https://business.facebook.com/latest/billing_hub/accounts?asset_id=${waba}`;
});

const bmInsightsUrl = computed(() => {
  const waba = wabaId.value;
  if (!waba)
    return 'https://business.facebook.com/latest/whatsapp_manager/insights';
  return `https://business.facebook.com/latest/whatsapp_manager/insights?waba_id=${waba}`;
});

const bmPhoneNumbersUrl = computed(() => {
  const waba = wabaId.value;
  if (!waba)
    return 'https://business.facebook.com/latest/whatsapp_manager/phone_numbers';
  return `https://business.facebook.com/latest/whatsapp_manager/phone_numbers?waba_id=${waba}`;
});

const bmOverviewUrl = computed(() => {
  const waba = wabaId.value;
  if (!waba)
    return 'https://business.facebook.com/latest/whatsapp_manager/overview';
  return `https://business.facebook.com/latest/whatsapp_manager/overview?waba_id=${waba}`;
});

const openLink = url => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const quickLinks = computed(() => [
  {
    title: 'Visão Geral',
    description: 'Painel principal da conta WhatsApp Business',
    icon: 'i-lucide-layout-dashboard',
    url: bmOverviewUrl.value,
    color: 'text-n-blue-11',
    bg: 'bg-n-blue-3',
  },
  {
    title: 'Hub de Cobrança',
    description: 'Saldo, cartões de pagamento e histórico',
    icon: 'i-lucide-credit-card',
    url: bmBillingUrl.value,
    color: 'text-n-teal-11',
    bg: 'bg-n-teal-3',
  },
  {
    title: 'Insights Completos',
    description: 'Benchmarks, recomendações e erros detalhados',
    icon: 'i-lucide-bar-chart-3',
    url: bmInsightsUrl.value,
    color: 'text-n-violet-11',
    bg: 'bg-n-violet-3',
  },
  {
    title: 'Números de Telefone',
    description: 'Gerenciar números e qualidade',
    icon: 'i-lucide-phone',
    url: bmPhoneNumbersUrl.value,
    color: 'text-n-amber-11',
    bg: 'bg-n-amber-3',
  },
]);

const fetchInsights = async () => {
  try {
    isLoading.value = true;
    const response = await InboxHealthAPI.getWhatsappBmInsights(
      props.inbox.id,
      startDate.value,
      endDate.value
    );
    insights.value = response.data;
  } catch (err) {
    useAlert(
      err.response?.data?.error || 'Erro ao carregar insights. Tente novamente.'
    );
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchInsights);
</script>

<template>
  <div class="flex flex-col gap-6 mx-6 max-w-5xl">
    <!-- Cabeçalho -->
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-medium text-n-slate-12">
        Business Manager
      </h3>
      <p class="text-sm text-n-slate-11">
        Insights da Meta e acesso rápido ao Business Manager.
      </p>
    </div>

    <!-- Informações da Conta -->
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-slate-2">
        <span class="text-xs font-medium text-n-slate-10">Inbox</span>
        <span class="text-sm font-semibold text-n-slate-12">
          {{ inboxName }}
        </span>
      </div>
      <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-slate-2">
        <span class="text-xs font-medium text-n-slate-10">Telefone</span>
        <span class="text-sm font-semibold text-n-slate-12">
          {{ phoneNumber || '-' }}
        </span>
      </div>
      <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-slate-2">
        <span class="text-xs font-medium text-n-slate-10">WABA ID</span>
        <span class="text-sm font-semibold text-n-slate-12">
          {{ wabaId || '-' }}
        </span>
      </div>
    </div>

    <!-- Filtro de período -->
    <div class="flex flex-wrap gap-3 items-end">
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">Data início</label>
        <input
          v-model="startDate"
          type="date"
          class="px-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-black1 text-n-slate-12"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">Data fim</label>
        <input
          v-model="endDate"
          type="date"
          class="px-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-black1 text-n-slate-12"
        />
      </div>
      <NextButton
        sm
        slate
        faded
        icon="i-lucide-refresh-cw"
        label="Atualizar"
        :is-loading="isLoading"
        @click="fetchInsights"
      />
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading && !insights"
      class="flex justify-center items-center py-12"
    >
      <Icon
        icon="i-lucide-loader-2"
        class="size-6 text-n-slate-10 animate-spin"
      />
    </div>

    <!-- Dados de Templates -->
    <template v-if="insights">
      <div v-if="templateTotals" class="flex flex-col gap-2">
        <h4 class="text-sm font-medium text-n-slate-12">
          Desempenho dos Templates
          <span class="text-xs font-normal text-n-slate-10">(via Meta API)</span>
        </h4>
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-slate-2">
            <span class="text-xs font-medium text-n-slate-10">Enviadas</span>
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ templateTotals.sent || 0 }}
            </span>
          </div>
          <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-slate-2">
            <span class="text-xs font-medium text-n-slate-10">Entregues</span>
            <span class="text-2xl font-semibold text-n-teal-11">
              {{ templateTotals.delivered || 0 }}
            </span>
          </div>
          <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-slate-2">
            <span class="text-xs font-medium text-n-slate-10">
              Taxa de Leitura
            </span>
            <span class="text-2xl font-semibold text-n-blue-11">
              {{ templateTotals.read_rate || 0 }}%
            </span>
            <span class="text-xs text-n-slate-10">
              {{ templateTotals.read || 0 }} lidas
            </span>
          </div>
          <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-slate-2">
            <span class="text-xs font-medium text-n-slate-10">
              Taxa de Cliques
            </span>
            <span class="text-2xl font-semibold text-n-violet-11">
              {{ templateTotals.click_rate || 0 }}%
            </span>
            <span class="text-xs text-n-slate-10">
              {{ templateTotals.clicked || 0 }} cliques
            </span>
          </div>
          <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-slate-2">
            <span class="text-xs font-medium text-n-slate-10">
              Custo Templates
            </span>
            <span class="text-2xl font-semibold text-n-amber-11">
              ${{ (templateTotals.cost || 0).toFixed(2) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Tabela de custo por Template -->
      <div v-if="templateList.length" class="flex flex-col gap-2">
        <h4 class="text-sm font-medium text-n-slate-12">
          Custo por Template
        </h4>
        <div class="overflow-x-auto rounded-lg border border-n-weak">
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-n-slate-2 text-left">
                <th class="px-4 py-2.5 text-xs font-medium text-n-slate-10">
                  Template
                </th>
                <th class="px-4 py-2.5 text-xs font-medium text-n-slate-10">
                  Categoria
                </th>
                <th
                  class="px-4 py-2.5 text-xs font-medium text-n-slate-10 text-right"
                >
                  Enviadas
                </th>
                <th
                  class="px-4 py-2.5 text-xs font-medium text-n-slate-10 text-right"
                >
                  Entregues
                </th>
                <th
                  class="px-4 py-2.5 text-xs font-medium text-n-slate-10 text-right"
                >
                  Leitura
                </th>
                <th
                  class="px-4 py-2.5 text-xs font-medium text-n-slate-10 text-right"
                >
                  Cliques
                </th>
                <th
                  class="px-4 py-2.5 text-xs font-medium text-n-slate-10 text-right"
                >
                  Custo/Msg
                </th>
                <th
                  class="px-4 py-2.5 text-xs font-medium text-n-slate-10 text-right"
                >
                  Custo Total
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="tpl in templateList"
                :key="tpl.template_id"
                class="border-t border-n-weak hover:bg-n-slate-2 transition-colors"
              >
                <td class="px-4 py-2.5 font-medium text-n-slate-12">
                  {{ tpl.name }}
                </td>
                <td class="px-4 py-2.5 text-n-slate-10">
                  <span
                    class="px-2 py-0.5 text-xs rounded-full"
                    :class="{
                      'bg-n-violet-3 text-n-violet-11':
                        tpl.category === 'MARKETING',
                      'bg-n-blue-3 text-n-blue-11': tpl.category === 'UTILITY',
                      'bg-n-amber-3 text-n-amber-11':
                        tpl.category === 'AUTHENTICATION',
                      'bg-n-slate-3 text-n-slate-11':
                        tpl.category !== 'MARKETING' &&
                        tpl.category !== 'UTILITY' &&
                        tpl.category !== 'AUTHENTICATION',
                    }"
                  >
                    {{ tpl.category }}
                  </span>
                </td>
                <td class="px-4 py-2.5 text-right text-n-slate-12">
                  {{ tpl.sent }}
                </td>
                <td class="px-4 py-2.5 text-right text-n-teal-11">
                  {{ tpl.delivered }}
                </td>
                <td class="px-4 py-2.5 text-right text-n-blue-11">
                  {{ tpl.read_rate }}%
                  <span class="text-xs text-n-slate-10">
                    ({{ tpl.read }})
                  </span>
                </td>
                <td class="px-4 py-2.5 text-right text-n-violet-11">
                  {{ tpl.click_rate }}%
                  <span class="text-xs text-n-slate-10">
                    ({{ tpl.clicked }})
                  </span>
                </td>
                <td class="px-4 py-2.5 text-right text-n-slate-11">
                  ${{ (tpl.cost_per_delivered || 0).toFixed(4) }}
                </td>
                <td class="px-4 py-2.5 text-right font-semibold text-n-amber-11">
                  ${{ (tpl.cost || 0).toFixed(2) }}
                </td>
              </tr>
              <!-- Linha de totais -->
              <tr
                class="border-t-2 border-n-strong bg-n-slate-2 font-semibold"
              >
                <td class="px-4 py-2.5 text-n-slate-12" colspan="2">
                  Total
                </td>
                <td class="px-4 py-2.5 text-right text-n-slate-12">
                  {{ templateTotals.sent }}
                </td>
                <td class="px-4 py-2.5 text-right text-n-teal-11">
                  {{ templateTotals.delivered }}
                </td>
                <td class="px-4 py-2.5 text-right text-n-blue-11">
                  {{ templateTotals.read_rate }}%
                </td>
                <td class="px-4 py-2.5 text-right text-n-violet-11">
                  {{ templateTotals.click_rate }}%
                </td>
                <td class="px-4 py-2.5 text-right text-n-slate-11">
                  -
                </td>
                <td class="px-4 py-2.5 text-right text-n-amber-11">
                  ${{ (templateTotals.cost || 0).toFixed(2) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="text-xs text-n-slate-10">
          Dados de leitura e cliques ficam disponíveis por até 7 dias após o
          envio. Custo por mensagem é o valor cobrado pela Meta por mensagem
          entregue.
        </p>
      </div>

      <!-- Pricing Analytics -->
      <div v-if="pricingData" class="flex flex-col gap-2">
        <h4 class="text-sm font-medium text-n-slate-12">
          Preços das Mensagens
          <span class="text-xs font-normal text-n-slate-10">(via Meta API)</span>
        </h4>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-slate-2">
            <span class="text-xs font-medium text-n-slate-10">
              Cobrança Total
            </span>
            <span class="text-2xl font-semibold text-n-slate-12">
              ${{ (pricingData.total_cost || 0).toFixed(2) }}
            </span>
          </div>
          <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-slate-2">
            <span class="text-xs font-medium text-n-slate-10">
              Total de Mensagens
            </span>
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ pricingData.total_volume || 0 }}
            </span>
          </div>
          <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-slate-2">
            <span class="text-xs font-medium text-n-slate-10">
              Mensagens Gratuitas
            </span>
            <span class="text-2xl font-semibold text-n-teal-11">
              {{ pricingData.free_volume || 0 }}
            </span>
          </div>
          <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-slate-2">
            <span class="text-xs font-medium text-n-slate-10">
              Mensagens Pagas
            </span>
            <span class="text-2xl font-semibold text-n-amber-11">
              {{ pricingData.paid_volume || 0 }}
            </span>
          </div>
        </div>

        <!-- Categorias -->
        <div
          v-if="pricingCategoryCards.length"
          class="grid grid-cols-2 gap-3 sm:grid-cols-4"
        >
          <div
            v-for="card in pricingCategoryCards"
            :key="card.label"
            class="flex gap-3 items-center p-3 rounded-lg bg-n-slate-2"
          >
            <div
              class="flex justify-center items-center rounded-lg size-9"
              :class="card.bg"
            >
              <Icon :icon="card.icon" class="size-4" :class="card.color" />
            </div>
            <div class="flex flex-col">
              <span class="text-sm font-semibold text-n-slate-12">
                {{ card.volume }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ card.label }} · {{ card.cost }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Sem dados da Meta -->
      <div
        v-if="!templateTotals && !pricingData"
        class="flex gap-2 items-start p-4 rounded-lg bg-n-slate-2"
      >
        <Icon
          icon="i-lucide-info"
          class="size-4 text-n-slate-10 flex-shrink-0 mt-0.5"
        />
        <p class="text-sm text-n-slate-11">
          Dados de insights não disponíveis para esta conta. Verifique se o token
          de acesso tem as permissões necessárias.
        </p>
      </div>
    </template>

    <!-- Acesso Rápido ao BM -->
    <div class="flex flex-col gap-2">
      <h4 class="text-sm font-medium text-n-slate-12">
        Acesso Rápido ao Business Manager
      </h4>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <button
          v-for="link in quickLinks"
          :key="link.title"
          class="flex gap-3 items-center p-4 rounded-lg bg-n-slate-2 text-left transition-colors hover:bg-n-slate-3 cursor-pointer border-0"
          @click="openLink(link.url)"
        >
          <div
            class="flex justify-center items-center rounded-lg size-10 flex-shrink-0"
            :class="link.bg"
          >
            <Icon :icon="link.icon" class="size-5" :class="link.color" />
          </div>
          <div class="flex flex-col gap-0.5 min-w-0">
            <span class="text-sm font-medium text-n-slate-12">
              {{ link.title }}
            </span>
            <span class="text-xs text-n-slate-10 truncate">
              {{ link.description }}
            </span>
          </div>
          <Icon
            icon="i-lucide-external-link"
            class="size-4 text-n-slate-9 flex-shrink-0 ml-auto"
          />
        </button>
      </div>
    </div>

    <!-- Nota -->
    <div class="flex gap-2 items-start p-3 rounded-lg bg-n-amber-2">
      <Icon
        icon="i-lucide-info"
        class="size-4 text-n-amber-11 flex-shrink-0 mt-0.5"
      />
      <p class="text-xs text-n-amber-11">
        Saldo e cartões de pagamento só estão disponíveis diretamente no
        Business Manager da Meta. Clique em "Hub de Cobrança" para adicionar
        ou gerenciar seus cartões.
      </p>
    </div>
  </div>
</template>
